import { alpha, createTheme, responsiveFontSizes, type Theme } from "@mui/material/styles";
import type { ClaimVerdict } from "@shared/types/claims.ts";

/** A theme colour that can tint a surface (an avatar). */
type TintColor = "primary" | "secondary" | "success" | "info" | "warning" | "error";

/** A background wash and the text drawn on it. */
interface Tint {
  background: string;
  color: string;
}

/** The kinds of evidence token marked in a document. */
type EvidenceTokenKind = "magnitude" | "significance";

declare module "@mui/material/styles" {
  interface Theme {
    verdicts: Record<ClaimVerdict, { main: string; light: string; contrastText: string }>;
    /** One accent color per taxonomy domain, by the domain's short key. */
    domains: Record<string, string>;
    /** For each theme colour, the wash it tints a surface with and the text on it. Set by
     *  `withSchemeTokens`, so it follows the theme's palette and colour scheme. */
    tints: Record<TintColor, Tint>;
    /** How a recognised evidence token is marked in a document. Set by `withSchemeTokens`. */
    evidenceTokens: Record<EvidenceTokenKind, Tint>;
    /** The mark on text a document highlights. Set by `withSchemeTokens`. */
    highlight: { background: string };
  }
  interface ThemeOptions {
    verdicts?: Record<ClaimVerdict, { main: string; light: string; contrastText: string }>;
    domains?: Record<string, string>;
    tints?: Record<TintColor, Tint>;
    evidenceTokens?: Record<EvidenceTokenKind, Tint>;
    highlight?: { background: string };
  }
}

const TINT_COLORS: TintColor[] = ["primary", "secondary", "success", "info", "warning", "error"];

/**
 * Adds the colours that are not part of MUI's palette but must follow the colour scheme (the tint
 * of an avatar, the marks in a document), worked out from the theme's own palette. Components
 * read them from the theme and never ask which scheme it is; this is the one place that does.
 * Call it on a theme once its palette is final.
 */
export function withSchemeTokens(theme: Theme): Theme {
  const dark = theme.palette.mode === "dark";
  const tints = Object.fromEntries(
    TINT_COLORS.map((color) => {
      const { main, light, dark: shade } = theme.palette[color];
      const tint: Tint = dark
        ? { background: alpha(main, 0.24), color: light }
        : { background: light, color: shade };
      return [color, tint];
    })
  ) as Record<TintColor, Tint>;

  return createTheme(theme, {
    tints,
    evidenceTokens: dark
      ? {
          magnitude: { color: "#C4B5FD", background: "rgba(167, 139, 250, 0.2)" },
          significance: { color: "#67E8F9", background: "rgba(34, 211, 238, 0.16)" },
        }
      : {
          magnitude: { color: "#6D28D9", background: "rgba(139, 92, 246, 0.14)" },
          significance: { color: "#0E7490", background: "rgba(14, 116, 144, 0.12)" },
        },
    highlight: { background: dark ? "rgba(255, 220, 0, 0.28)" : "rgba(255, 220, 0, 0.5)" },
  });
}

// Editorial restyle (doc 020). Two typefaces: a warm serif (Fraunces) carries the
// editorial / "written record" voice on every heading; a neutral grotesque (Inter)
// carries body + dense UI. Both are self-hosted variable fonts (see main.tsx).
const SERIF = '"Fraunces Variable", Georgia, "Times New Roman", serif';
const SANS = '"Inter Variable", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

// Soft, layered ambient shadows — surfaces lift off the warm canvas without a
// hard drop shadow. This is the single biggest "this is a product, not a
// prototype" signal alongside the type.
export const softShadow = "0 1px 2px rgba(40, 38, 32, 0.04), 0 8px 24px rgba(40, 38, 32, 0.06)";
export const softShadowLifted =
  "0 2px 6px rgba(40, 38, 32, 0.05), 0 16px 40px rgba(40, 38, 32, 0.10)";

// Brand sage is fixed; everything else is tuned around it.
const SAGE = "#77A290";

const heading = { fontFamily: SANS, fontWeight: 600, letterSpacing: "-0.015em" };

export const baseTheme = withSchemeTokens(
  createTheme({
    palette: {
      primary: {
        main: SAGE,
        light: "#9BBFAE",
        dark: "#4F7A66",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: "#4A5568",
      },
      // Green-tinted "ink" rather than neutral gray, so text reads as part of the
      // brand world instead of a default stylesheet.
      text: {
        primary: "#1C211C",
        secondary: "#5C625A",
      },
      background: {
        paper: "#FFFFFF", // raised surfaces
        default: "#F4F1EA", // warm canvas
      },
      divider: "rgba(40, 38, 32, 0.08)",
    },
    typography: {
      fontFamily: SANS,
      fontSize: 16,
      h1: heading,
      h2: heading,
      h3: heading,
      h4: heading,
      h5: heading,
      h6: { ...heading, letterSpacing: "-0.01em" },
    },
    shape: {
      borderRadius: 12,
    },
    domains: {
      health: "#1565c0",
    },
    verdicts: {
      SUPPORTED: { main: "#77A290", light: "#E4F0EB", contrastText: "#1A3D2F" },
      LEANING_SUPPORTED: { main: "#A8C3A0", light: "#EBF3E8", contrastText: "#2A4A32" },
      LEANING_CONTRADICTED: { main: "#C4785A", light: "#F5EAE4", contrastText: "#FFFFFF" },
      MIXED: { main: "#5A7BA8", light: "#E0EAF5", contrastText: "#1A3050" },
      CONTRADICTED: { main: "#993C1D", light: "#F5E5DE", contrastText: "#FFFFFF" },
      INSUFFICIENT: { main: "#888780", light: "#EDEDEB", contrastText: "#3A3A36" },
      INCONCLUSIVE: { main: "#9C9484", light: "#F0EEE8", contrastText: "#3A3630" },
      NO_COVERAGE: { main: "#888780", light: "#EDEDEB", contrastText: "#3A3A36" },
      /** @deprecated */
      PARTIALLY_SUPPORTED: { main: "#EF9F27", light: "#FDF0D8", contrastText: "#5C3D00" },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { scrollBehavior: "smooth" },
          body: {
            backgroundColor: "#F4F1EA",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          // Variable serif: let the optical-size axis track the rendered size.
          "h1, h2, h3, h4, h5, h6": { fontOpticalSizing: "auto" },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 999, // pill
          },
          sizeLarge: {
            paddingTop: 12,
            paddingBottom: 12,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
        },
      },
      MuiTab: {
        styleOverrides: {
          // Sentence case like buttons, and a label that sits close to the indicator.
          root: {
            textTransform: "none",
            fontWeight: 600,
            minHeight: 40,
            paddingTop: 6,
            paddingBottom: 6,
          },
        },
      },
      MuiToggleButton: {
        // As compact as a tab, in sentence case. The selected one is lettered in the primary
        // colour, like the selected tab; its background does not change.
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 4,
            // An icon before the label sits level with it, at the size of the text.
            gap: 6,
            "& > svg": { fontSize: "1.15rem" },
            paddingTop: 3,
            paddingBottom: 3,
            paddingLeft: 12,
            paddingRight: 12,
            "&.Mui-selected, &.Mui-selected:hover": {
              color: theme.palette.primary.main,
              backgroundColor: "transparent",
            },
          }),
        },
      },
      MuiChip: {
        // Chips are compact metadata tags by default, not buttons.
        defaultProps: { size: "small" },
        styleOverrides: {
          // Squared corners (MUI's default is a fully-rounded pill, which reads as clickable)
          // and a slight letter-spacing. Filled colour variants render as a soft tint — a light
          // wash of the colour with dark text — rather than a saturated fill, so a row of tags
          // reads as calm metadata. Interactive chips (clickable / deletable) deepen the wash on
          // hover, keeping them distinguishable; the round Pill primitive overrides its own radius.
          sizeSmall: {
            fontSize: "0.7rem",
            height: 20,
          },
          root: ({ theme, ownerState }) => {
            const { color, variant } = ownerState;
            const soft =
              variant === "filled" && color && color !== "default"
                ? {
                    backgroundColor: alpha(theme.palette[color].main, 0.16),
                    color: theme.palette[color].dark,
                    ...(ownerState.onDelete || ownerState.clickable
                      ? {
                          "&:hover": {
                            backgroundColor: alpha(theme.palette[color].main, 0.26),
                          },
                        }
                      : {}),
                  }
                : {};
            return {
              fontWeight: 600,
              borderRadius: 4,
              letterSpacing: "0.02em",
              ...soft,
            };
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: "#FFFFFF",
            // Soft focus ring in sage instead of the default 2px hard outline.
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: SAGE,
              borderWidth: 1,
              boxShadow: `0 0 0 3px ${SAGE}33`,
            },
          },
          notchedOutline: {
            borderColor: "rgba(40, 38, 32, 0.12)",
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          variant: "outlined",
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderColor: "transparent",
            padding: "12px 16px",
          },
        },
      },
    },
  })
);

export const theme = responsiveFontSizes(baseTheme, { factor: 3 });
